import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const DemoSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const sectionRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    teamSize: '',
    agreedToTerms: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Введите ваше имя';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Введите email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Некорректный email';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Введите телефон';
    } else if (!/^[\d\s\+\-\(\)]+$/?.test(formData?.phone)) {
      newErrors.phone = 'Некорректный номер телефона';
    }

    if (!formData?.company?.trim()) {
      newErrors.company = 'Введите название компании';
    }

    if (!formData?.agreedToTerms) {
      newErrors.agreedToTerms = 'Необходимо согласие с условиями';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          industry: '',
          teamSize: '',
          agreedToTerms: false
        });
      }, 5000);
    }
  };

  const industries = [
    { value: '', label: 'Выберите отрасль' },
    { value: 'retail', label: 'Розничная торговля' },
    { value: 'manufacturing', label: 'Производство' },
    { value: 'services', label: 'Услуги' },
    { value: 'it', label: 'IT и технологии' },
    { value: 'construction', label: 'Строительство' },
    { value: 'logistics', label: 'Логистика' },
    { value: 'other', label: 'Другое' }
  ];

  const teamSizes = [
    { value: '', label: 'Размер команды' },
    { value: '1-10', label: '1-10 сотрудников' },
    { value: '11-50', label: '11-50 сотрудников' },
    { value: '51-200', label: '51-200 сотрудников' },
    { value: '201-500', label: '201-500 сотрудников' },
    { value: '500+', label: '500+ сотрудников' }
  ];

  return (
    <section id="demo" ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-card via-background to-card" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Запланируйте демонстрацию
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Увидьте СФЕРА в действии и узнайте, как платформа может трансформировать ваш бизнес
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className={`transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="glassmorphism-strong rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-6">Что вы получите</h3>
              
              <div className="space-y-6">
                {[
                  {
                    icon: 'Video',
                    title: 'Персональная демонстрация',
                    description: '45-минутная презентация возможностей СФЕРА с учетом специфики вашего бизнеса'
                  },
                  {
                    icon: 'Users',
                    title: 'Консультация эксперта',
                    description: 'Ответы на все вопросы от специалиста по автоматизации бизнес-процессов'
                  },
                  {
                    icon: 'FileText',
                    title: 'Индивидуальное предложение',
                    description: 'Расчет стоимости и ROI для вашей компании с учетом всех требований'
                  },
                  {
                    icon: 'Zap',
                    title: 'План внедрения',
                    description: 'Детальный план миграции данных и обучения команды с конкретными сроками'
                  }
                ]?.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-1">{item?.title}</h4>
                      <p className="text-sm text-muted-foreground">{item?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glassmorphism rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Clock" size={20} color="var(--color-accent)" className="mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground mb-2">Доступные слоты</h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div>• Сегодня: 14:00, 16:30, 18:00</div>
                    <div>• Завтра: 10:00, 12:00, 15:00, 17:30</div>
                    <div>• Или выберите удобное время в форме</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            {showSuccess ? (
              <div className="glassmorphism-strong rounded-2xl p-8 text-center animate-fade-in">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-success/10 flex items-center justify-center">
                  <Icon name="CheckCircle2" size={48} color="var(--color-success)" />
                </div>
                <h3 className="text-3xl font-black text-foreground mb-4">
                  Заявка принята!
                </h3>
                <p className="text-muted-foreground mb-6">
                  Спасибо за интерес к СФЕРА! Наш менеджер свяжется с вами в течение 15 минут для подтверждения времени демонстрации.
                </p>
                <div className="glassmorphism rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-foreground mb-4">Пока вы ждете:</h4>
                  <div className="space-y-3 text-left">
                    <a href="#" className="flex items-center space-x-3 p-3 glassmorphism rounded-lg hover:bg-muted/20 transition-colors">
                      <Icon name="Play" size={20} color="var(--color-primary)" />
                      <span className="text-sm text-foreground">Посмотрите видеообзор платформы</span>
                    </a>
                    <a href="#" className="flex items-center space-x-3 p-3 glassmorphism rounded-lg hover:bg-muted/20 transition-colors">
                      <Icon name="FileText" size={20} color="var(--color-accent)" />
                      <span className="text-sm text-foreground">Изучите кейсы клиентов</span>
                    </a>
                    <a href="#" className="flex items-center space-x-3 p-3 glassmorphism rounded-lg hover:bg-muted/20 transition-colors">
                      <Icon name="Download" size={20} color="var(--color-secondary)" />
                      <span className="text-sm text-foreground">Скачайте презентацию СФЕРА</span>
                    </a>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={() => setShowSuccess(false)}
                >
                  Закрыть
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="glassmorphism-strong rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-foreground mb-6">Заполните форму</h3>
                
                <div className="space-y-4">
                  <Input
                    label="Ваше имя"
                    type="text"
                    name="name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    error={errors?.name}
                    placeholder="Иван Петров"
                    required
                  />

                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    error={errors?.email}
                    placeholder="ivan@company.ru"
                    required
                  />

                  <Input
                    label="Телефон"
                    type="tel"
                    name="phone"
                    value={formData?.phone}
                    onChange={handleInputChange}
                    error={errors?.phone}
                    placeholder="+7 (999) 123-45-67"
                    description="Для связи в Telegram или звонка"
                    required
                  />

                  <Input
                    label="Компания"
                    type="text"
                    name="company"
                    value={formData?.company}
                    onChange={handleInputChange}
                    error={errors?.company}
                    placeholder="ООО 'Название'"
                    required
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Отрасль
                      </label>
                      <select
                        name="industry"
                        value={formData?.industry}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                      >
                        {industries?.map(option => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Размер команды
                      </label>
                      <select
                        name="teamSize"
                        value={formData?.teamSize}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                      >
                        {teamSizes?.map(option => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="pt-2">
                    <Checkbox
                      label="Я согласен с условиями обработки персональных данных и политикой конфиденциальности"
                      checked={formData?.agreedToTerms}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, agreedToTerms: e?.target?.checked }));
                        if (errors?.agreedToTerms) {
                          setErrors(prev => ({ ...prev, agreedToTerms: '' }));
                        }
                      }}
                      error={errors?.agreedToTerms}
                      size="sm"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="Send"
                    iconPosition="right"
                    className="neon-glow-primary mt-6"
                  >
                    Запланировать демо
                  </Button>

                  <p className="text-xs text-center text-muted-foreground mt-4">
                    Нажимая кнопку, вы соглашаетесь с условиями обработки данных
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;